import { useState, useEffect } from 'react';
import { Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// UNE image pertinente par catégorie (correspondance exacte avec les 19 catégories)
const CATEGORY_IMAGES = {
  'referent-alliance-humaine': {
    name: 'Référent Alliance Humaine',
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600'  // Équipe solidaire
  },
  'alimentaire': {
    name: 'Alimentaire',
    image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=600'  // Fruits et légumes
  },
  'bricolage': {
    name: 'Bricolage',
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600'  // Outils bricolage
  },
  'cours': {
    name: 'Cours',
    image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600'  // Classe/enseignement
  },
  'demarches-administratives': {
    name: 'Démarches Administratives',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600'  // Documents administratifs
  },
  'espace-vert': {
    name: 'Espace Vert',
    image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600'  // Jardinage
  },
  'gestion-quotidien': {
    name: 'Gestion du Quotidien',
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600'  // Organisation quotidienne
  },
  'jeux-jouets': {
    name: 'Jeux & Jouets',
    image: 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600'  // Jouets enfants
  },
  'logement': {
    name: 'Logement',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600'  // Maison/logement
  },
  'materiel-informatique': {
    name: 'Matériel Informatique',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600'  // Ordinateur/tech
  },
  'medical-paramedical': {
    name: 'Médical & Paramédical',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=600'  // Médical/santé
  },
  'passions': {
    name: 'Passions',
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=600'  // Arts/créativité
  },
  'pret-location': {
    name: 'Prêt & Location',
    image: 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?w=600'  // Échange/partage
  },
  'services-exterieurs': {
    name: 'Services Extérieurs',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600'  // Services professionnels
  },
  'transport': {
    name: 'Transport',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600'  // Voiture/transport
  },
  'vetements-linge-chaussures': {
    name: 'Vêtements, Linge & Chaussures',
    image: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=600'  // Vêtements
  },
  'lecture': {
    name: 'Lecture',
    image: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=600'  // Livres
  },
  'loisirs-culturels': {
    name: 'Loisirs Culturels',
    image: 'https://images.unsplash.com/photo-1478147427282-58a87a120781?w=600'  // Culture/spectacle
  },
  'bebe-enfant': {
    name: 'Bébé & Enfant',
    image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600'  // Bébé/enfant
  }
};

const OfferImageSelector = ({ category, selectedImage, onImageSelect }) => {
  const [showPredefined, setShowPredefined] = useState(true);
  const [customImage, setCustomImage] = useState(null);

  // Récupérer l'image prédéfinie pour cette catégorie
  const categoryInfo = CATEGORY_IMAGES[category];
  const categoryImageUrl = categoryInfo?.image || CATEGORY_IMAGES['referent-alliance-humaine'].image;
  const categoryName = categoryInfo?.name || 'Catégorie';

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast.error('Erreur', { description: 'L\'image doit faire moins de 5MB' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomImage(reader.result);
        onImageSelect(reader.result);
        setShowPredefined(false);
        toast.success('Image sélectionnée !', { description: 'Votre image a été chargée' });
      };
      reader.readAsDataURL(file);
    }
  };

  // Auto-sélectionner l'image de catégorie si aucune image n'est sélectionnée
  useEffect(() => {
    if (!selectedImage && showPredefined && categoryImageUrl) {
      onImageSelect(categoryImageUrl);
    }
  }, [category, categoryImageUrl]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Image de l'offre</label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowPredefined(!showPredefined)}
          className="text-purple-600"
        >
          {showPredefined ? '📤 Uploader ma photo' : '🎨 Image suggérée'}
        </Button>
      </div>

      {showPredefined ? (
        <div className="space-y-3">
          <div className="text-sm text-gray-600 font-medium mb-2">
            📸 Image suggérée pour : <span className="text-purple-600">{categoryName}</span>
          </div>
          <button
            type="button"
            onClick={() => onImageSelect(categoryImageUrl)}
            className={`relative w-full aspect-video rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.01] max-h-40 ${
              selectedImage === categoryImageUrl
                ? 'border-purple-500 ring-2 ring-purple-300'
                : 'border-gray-300 hover:border-purple-400'
            }`}
          >
            <img
              src={categoryImageUrl}
              alt={categoryName}
              className="w-full h-full object-cover"
            />
            {selectedImage === categoryImageUrl && (
              <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                <div className="bg-purple-500 text-white rounded-full p-2">
                  <ImageIcon className="w-5 h-5" />
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
              <p className="text-white font-semibold text-xs">{categoryName}</p>
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {customImage && (
            <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-purple-500">
              <img
                src={customImage}
                alt="Image uploadée"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
                ✓
              </div>
            </div>
          )}
          <label className="cursor-pointer">
            <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 hover:border-purple-500 hover:bg-purple-50/50 transition-all text-center">
              <Upload className="w-12 h-12 mx-auto mb-3 text-purple-500" />
              <p className="text-sm font-medium text-gray-700 mb-1">
                {customImage ? 'Changer l\'image' : 'Cliquez pour uploader votre image'}
              </p>
              <p className="text-xs text-gray-500">
                PNG, JPG jusqu'à 5MB
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>
      )}

      {selectedImage && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <ImageIcon className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700 font-medium">
            Image sélectionnée ✓
          </span>
        </div>
      )}
    </div>
  );
};

export default OfferImageSelector;
